import { ArdoqId } from '@ardoq/api-types';
import { NextSelectionContext, NodeModel } from './types';
import { WorkspaceNode } from './WorkspaceNode';
import { ComponentNode } from './ComponentNode';
import { ScenarioNode } from './ScenarioNode';
import { CreateComponentNode } from './CreateComponentNode';
import { Features } from '@ardoq/features';
import { logError } from '@ardoq/logging';
import { isTruthy, isSameSet } from '@ardoq/common-helpers';
import { getDOMNodeInNavigatorWithDataId } from '../getDOMNodeInNavigatorWithDataId';
import { NavigatorViewInterface } from '../NavigatorViewInterface';
import { getNavigatorStubViewInterface } from '../getNavigatorStubViewInterface';

const createNode = (
  tree: Tree,
  nodeId: string,
  parent: NodeModel | null = null
): NodeModel => {
  if (tree.navigatorViewInterface.isWorkspace(nodeId))
    return new WorkspaceNode(tree, nodeId, parent);
  return new ComponentNode(tree, nodeId, parent);
};

const getOrCreateNode = (
  tree: Tree,
  nodeId: string,
  parent: NodeModel | null = null
) => {
  if (tree && tree.hasNode(nodeId)) {
    const node = tree.getNode(nodeId);
    node.parent = parent;
    return node;
  }
  const node = createNode(tree, nodeId, parent);
  if (tree) {
    tree.setNode(node);
  }
  return node;
};

const getOrCreateScenarioNode = (
  tree: Tree,
  nodeId: string,
  childrenIds: string[]
): NodeModel => {
  if (tree && tree.hasNode(nodeId)) {
    const node = tree.getNode(nodeId);
    if (
      isSameSet(
        node.getChildren().map(child => child.id),
        childrenIds
      )
    ) {
      return node;
    }
    tree.removeNode(nodeId);
  }
  const node = new ScenarioNode(tree, nodeId, childrenIds);
  tree.setNode(node);
  return node;
};

export const expandNode = (node: NodeModel) => {
  let currentNode = node;
  while (currentNode.parent) {
    currentNode.parent.expand();
    currentNode = currentNode.parent;
  }
};

export type TreeOptions = {
  hasCreateComponent: boolean;
  isAsyncSearch: boolean;
};

export const DEFAULT_TREE_OPTIONS: TreeOptions = {
  hasCreateComponent: false,
  isAsyncSearch: false,
};

export class Tree {
  public selectedContext: NodeModel | null;
  public activeWorkspace: NodeModel | null;
  public navigatorViewInterface: NavigatorViewInterface =
    getNavigatorStubViewInterface();

  keyboardSelectedNodeId: string | null = null;
  hoveredNodeId: string | null = null;
  isSelectedByKeyboard: boolean = false;

  private _idNodeMap: Map<string, NodeModel>;
  private _children: NodeModel[];
  private _selection: NodeModel[];
  private _hasChangedSet: Set<NodeModel>;
  private _expiredIds: Set<ArdoqId>;
  private _hiddenNodes: Set<NodeModel>;
  private _filterTerm: string | null = null;
  private _rawFilterTerm: string | null = null;
  private _externalFilter: ((id: string) => boolean) | null = null;
  private _disabledOptions: Set<string> = new Set();
  private _isAllSelected: boolean = false;
  private _hasExactFilterMatch: boolean = false;
  private _createComponentNode: CreateComponentNode | null;
  private _isAsyncSearch: boolean;

  constructor({ hasCreateComponent, isAsyncSearch }: TreeOptions) {
    this.selectedContext = null;
    this.activeWorkspace = null;
    this._hiddenNodes = new Set();
    this._idNodeMap = new Map<string, NodeModel>();
    this._children = [];
    this._selection = [];
    this._hasChangedSet = new Set<NodeModel>();
    this._expiredIds = new Set<ArdoqId>();
    this._createComponentNode = hasCreateComponent
      ? new CreateComponentNode(this)
      : null;
    this._isAsyncSearch = isAsyncSearch;
  }
  // Bound instance member so it can be used directly as a callback.
  getNextSelectionCandidate: (selection?: string[]) => string | null = (
    selection?: string[]
  ) => {
    const nodeSelection = selection
      ? selection.map(id => this.getNode(id)).filter(isTruthy)
      : null;

    const { nextSelection } = this.getChildren().reduce<NextSelectionContext>(
      (nextSelectionContext, node) =>
        node.getNextSelectionCandidate(nextSelectionContext),
      {
        selection: new Set(nodeSelection || this._selection),
        nextSelection: this.getChildren()[0],
        isLastSet: false,
      }
    );
    if (nextSelection instanceof ComponentNode) {
      return nextSelection.id;
    }
    return null;
  };

  reload() {
    this.getChildren().forEach(child => child.reload());
    if (this.navigatorViewInterface.isInDiffMode()) {
      this.updateAggregatedVisualDiffState();
    }
  }

  updateAggregatedVisualDiffState() {
    this.getChildren().forEach(child =>
      child.updateAggregatedVisualDiffState()
    );
  }

  // this is an instance method in order to avoid a circular dependency.
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  getOrCreateNode(tree: Tree, nodeId: string, parent: NodeModel | null = null) {
    return getOrCreateNode(tree, nodeId, parent);
  }

  setHasChanged(ids?: string[]) {
    if (ids) {
      ids.forEach(id => {
        const node = this._idNodeMap.get(id);
        if (node) {
          this._hasChangedSet.add(node);
          node.hasChanged = true;
        }
      });
    }
  }

  clearHasChanged() {
    this._hasChangedSet.forEach(node => (node.hasChanged = false));
    this._hasChangedSet.clear();
  }

  updateRange(id1: string, id2: string) {
    this.clearSelection();
    const [node1, node2] = [id1, id2].map(id => this._idNodeMap.get(id));
    const rangeBoundaries = new Set<NodeModel>();
    [node1, node2].filter(isTruthy).forEach(node => {
      rangeBoundaries.add(node);
    });
    if (rangeBoundaries.size === 2) {
      this.clearSelection();
      this.getChildren().reduce(
        (isRangeOpen, child) =>
          child.updateSelectionRange(
            this._selection,
            rangeBoundaries,
            isRangeOpen
          ),
        false
      );
    }
    return this._selection.map(node => node.id);
  }

  onlyContextNodeIsSelected(selectedNodes = this._selection) {
    return selectedNodes.length === 1 && selectedNodes[0].isSelectedContext;
  }

  syncSelection(ids: string[]) {
    this.clearSelection();
    const selectedNodes = ids.map(id => this.getNode(id)).filter(isTruthy);

    // we don't want the selected appearance when only the context node is selected.
    if (!this.onlyContextNodeIsSelected(selectedNodes)) {
      selectedNodes.forEach(node => (node.isSelected = true));
    }
    this._selection.push(...selectedNodes);
  }

  getSelection() {
    return this._selection.slice();
  }

  clearSelection() {
    this._selection.forEach(node => (node.isSelected = false));
    this._selection.length = 0;
  }

  hasNode(id: string) {
    return this._idNodeMap.has(id);
  }

  getNode(id: string) {
    return this._idNodeMap.get(id)!;
  }

  setNode(node: NodeModel) {
    this._idNodeMap.set(node.id, node);
  }

  getNodeByDomNode(domNode: Element) {
    const id = getDOMNodeInNavigatorWithDataId(domNode);
    return id ? this.getNode(id) : null;
  }

  getChildren() {
    return this._children;
  }

  addWorkspaceNode(refNodeId: string) {
    const node = getOrCreateNode(this, refNodeId, null);
    this._children.push(node);
    return node;
  }

  addScenarioNode(scenarioId: string, refNodeIds: string[]) {
    const node = getOrCreateScenarioNode(this, scenarioId, refNodeIds);
    node.updateChildren();
    this._children.push(node);
    return node;
  }

  removeNode(id: string) {
    const node = this._idNodeMap.get(id);
    if (!node) {
      return;
    }
    if (node.parent && node.parent.getChildren().includes(node)) {
      throw new Error('Trying to remove a node which is still in the tree');
    }
    this._idNodeMap.delete(id);
    const index = this._selection.findIndex(node => node.id === id);
    if (index > -1) {
      this._selection.splice(index, 1);
    }
  }

  _hasScenarioFeature() {
    if (
      this.navigatorViewInterface.hasFeature(Features.USE_NEW_PRICING_MODULES)
    ) {
      return (
        this.navigatorViewInterface.hasFeature(
          Features.TRANSFORMATION_MODULE
        ) && this.navigatorViewInterface.hasFeature(Features.SCENARIOS_BETA)
      );
    }
    return this.navigatorViewInterface.hasFeature(Features.SCENARIOS_BETA);
  }

  _hasAIGeneratedScenariosFeature() {
    return this.navigatorViewInterface.hasFeature(Features.AI_CHAT);
  }

  resetRootNodes(refNodeIds: string[]) {
    this._idNodeMap.clear();
    this._children.length = 0;
    const rootNodes = refNodeIds.map(refNodeId =>
      this.addWorkspaceNode(refNodeId)
    );
    this._expandRootNodes(rootNodes);
    this._recomputeExactFilterMatch();
  }

  private _shouldExpandDeep(): boolean {
    return this._isAsyncSearch && Boolean(this._filterTerm);
  }

  private _expandRootNodes(rootNodes: NodeModel[]) {
    rootNodes.forEach(node =>
      this._shouldExpandDeep() ? node.expandDeep() : node.expand()
    );
  }

  resetRootNodesKeepExpanded(refNodeIds: string[]) {
    const wasExpandedById = new Map<string, boolean>(
      Array.from(this._idNodeMap.values()).map(node => [
        node.id,
        node.isCollapsed === false,
      ])
    );
    this.resetRootNodes(refNodeIds);
    this._idNodeMap.forEach(node => {
      const wasExpanded = wasExpandedById.get(node.id);
      if (wasExpanded === undefined) {
        return;
      }
      if (wasExpanded) {
        node.expand();
      } else {
        node.collapse();
      }
    });
  }

  syncRootNodes(
    refNodeIds: string[],
    scenarioId = this.navigatorViewInterface.getActiveScenarioId()
  ) {
    this.removeExpiredNodes();
    const currentRootNodeMap = new Set(this._children.map(node => node.id));
    this.setAllExpired();
    this._children.length = 0;

    if (
      (this._hasScenarioFeature() || this._hasAIGeneratedScenariosFeature()) &&
      scenarioId
    ) {
      if (scenarioId) {
        const scenarioNode = this.addScenarioNode(scenarioId, refNodeIds);

        this.clearExpiredOfRootNodesAndClearExpired([scenarioNode]);
        const allNodes = Array.from(this._idNodeMap.values());
        if (!allNodes.some(node => node.isCollapsed === false)) {
          scenarioNode.expandDeep();
        }
      } else {
        logError(Error('Scenario not found.'), null, { scenarioId });
      }
    } else {
      const rootNodes = refNodeIds.map(refNodeId =>
        this.addWorkspaceNode(refNodeId)
      );
      rootNodes
        .filter(node => !currentRootNodeMap.has(node.id))
        .forEach(node => node.expand());
      this.clearExpiredOfRootNodesAndClearExpired(rootNodes);
    }
    this._recomputeExactFilterMatch();
  }

  // this function implements a sort of undocumented interface where Tree | NodeModel are used interchangeably by navigationManager.
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  isIncludedInContextByFilter() {
    return true;
  }

  setIsExpired() {
    this.setAllExpired();
  }

  cacheHiddenNodes(hiddenNodes: Set<NodeModel>) {
    this._hiddenNodes = hiddenNodes;
  }

  isNodeHidden(node: NodeModel) {
    return this._hiddenNodes.has(node);
  }

  hasNoFilterMatches() {
    return (
      Boolean(this._filterTerm) &&
      !this._createComponentNode &&
      this._children.every(child => this.isNodeHidden(child))
    );
  }

  setFilter(term: string) {
    this._rawFilterTerm = term;
    this._filterTerm = term.toLowerCase();
    Array.from(this._idNodeMap.values())
      .filter(node => node.isIncludedInContextByFilter())
      .forEach(expandNode);
    if (this._shouldExpandDeep()) {
      this._children.forEach(node => node.expandDeep());
    }
    this._recomputeExactFilterMatch();
  }

  getFilterTerm() {
    return this._filterTerm;
  }

  getRawFilterTerm() {
    return this._rawFilterTerm;
  }

  setExternalFilter(filter: ((id: string) => boolean) | null) {
    this._externalFilter = filter;
  }

  clearFilter() {
    this._filterTerm = null;
    this._rawFilterTerm = null;
    this._hasExactFilterMatch = false;
    if (this._isAsyncSearch) {
      this._children.forEach(node => node.collapseDeep());
      this._expandRootNodes(this._children);
    }
  }

  isIncludedInExternalFilter(node: NodeModel) {
    if (this._externalFilter) {
      return this._externalFilter(node.id);
    }
    return true;
  }

  isIncludedInFilterTerm(node: NodeModel) {
    if (this._filterTerm) {
      const filterTerm = node.getName();
      return (
        filterTerm.toLowerCase().includes(this._filterTerm) &&
        (!this._externalFilter || this._externalFilter(node.id))
      );
    }
    if (this._externalFilter) {
      return this._externalFilter(node.id);
    }
    return true;
  }

  getFirstComponentNode() {
    return this.getFirstComponentNodeInternal(this);
  }

  setDisabledOptions(disabledOptions: Set<string>) {
    this._disabledOptions = disabledOptions;
  }

  isNodeDisabledOption(node: NodeModel): boolean {
    // A node that cannot be picked at all reports no reason of its own — under
    // strict selection that is every node the external filter leaves out. Where
    // selection is not strict each listed node is an option in its own right,
    // including the ones only listed to give a match its hierarchy context, and
    // the same rules decide all of them.
    if (!node.isSelectable) return false;
    return this._disabledOptions.has(node.id);
  }

  setIsAllSelected(isAllSelected: boolean) {
    this._isAllSelected = isAllSelected;
  }

  getIsAllSelected() {
    return this._isAllSelected;
  }

  getCreateComponentNode() {
    return this._createComponentNode;
  }

  hasExactFilterMatch() {
    return this._hasExactFilterMatch;
  }

  private _recomputeExactFilterMatch() {
    if (!this._filterTerm) {
      this._hasExactFilterMatch = false;
      return;
    }
    const filterTerm = this._filterTerm;
    this._hasExactFilterMatch = Array.from(this._idNodeMap.values()).some(
      node => node.getName().toLowerCase() === filterTerm
    );
  }

  private getFirstComponentNodeInternal(node: {
    getChildren: () => NodeModel[];
  }): ComponentNode | null {
    for (const child of node.getChildren()) {
      if (
        child instanceof ComponentNode &&
        this.isIncludedInFilterTerm(child)
      ) {
        return child;
      }
      const result = this.getFirstComponentNodeInternal(child);
      if (result) {
        return result;
      }
    }
    return null;
  }

  private setAllExpired() {
    this._expiredIds = new Set(this._idNodeMap.keys());
  }

  private clearExpiredOfRootNodesAndClearExpired(rootNodes: NodeModel[]) {
    rootNodes.forEach(rootNode => this.clearExpired(rootNode));
    this.removeExpiredNodes();
  }

  private clearExpired(node: NodeModel) {
    this._expiredIds.delete(node.id);
    node.getChildren().forEach(node => this.clearExpired(node));
  }

  private removeExpiredNodes() {
    this._expiredIds.forEach(id => this._idNodeMap.delete(id));
    this._expiredIds.clear();
  }
}
